import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import StarRating from "../components/StarRating";
import SupplierImage from "../components/SupplierImage";
import { useNavigate, useParams } from "react-router-dom";
import {
  createSubscriptionCheckoutSession,
  getSubscriptionActivationDetails,
} from "../api/subscriptionApi";
import "./css/AbbonamentiCliente.css";
import "./css/AbbonamentoAttiva.css";

const euro = (cents) => {
  const value = Number(cents);
  if (!Number.isFinite(value)) return "—";

  return (value / 100).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

function getPresentation(plan) {
  return plan?.config?.presentation || {};
}

function getPlanTitle(plan) {
  return getPresentation(plan).title || plan?.name || "Abbonamento";
}

function getPlanShort(plan) {
  return getPresentation(plan).short_description || plan?.description || "";
}

function getPlanColor(plan) {
  return getPresentation(plan).theme_color || "#0877d9";
}

// Riconosce la riga "prezzo" tra quelle configurate in admin (icona o label),
// per poterla escludere: il prezzo in anteprima è SEMPRE ricalcolato qui
// sotto, mai preso dal testo statico salvato in admin (che è mensile e non
// si aggiorna da solo se il prezzo cambia). Stessa logica di AbbonamentiCliente.jsx.
function isPriceRow(row) {
  return (
    row.icon === "💶" ||
    row.icon === "🏷️" ||
    /costo|prezzo/i.test(row.label || "")
  );
}

function getPlanRows(plan) {
  const configuredRows = getPresentation(plan).rows;

  const featureRows =
    Array.isArray(configuredRows) && configuredRows.length
      ? configuredRows
          .map((row) => ({
            icon: row.icon || "•",
            label: row.label || "",
            value: row.value || "",
          }))
          .filter(
            (row) => (row.icon || row.label || row.value) && !isPriceRow(row),
          )
      : [
          {
            icon: "👕",
            label: "N° capi max",
            value:
              plan?.config?.max_items != null
                ? String(plan.config.max_items)
                : "—",
          },
        ];

  return [
    ...featureRows,
    {
      icon: "🏷️",
      label: "Costo a settimana",
      // SOLO VISIVO (anteprima piano): l'addebito reale resta mensile —
      // vedi la sezione "2. Pagamento" più sotto, che mostra ancora
      // plan.price_cents per intero e NON va toccata (è il riepilogo del
      // reale importo Stripe, deve restare inequivocabile).
      value: euro(plan?.price_cents / 4),
    },
  ];
}

function imageForPlan(plan) {
  const key = getPresentation(plan).image_key;

  if (key === "folded-small") return "/images/folded-small.png";
  if (key === "folded-medium") return "/images/folded-medium.png";
  if (key === "folded-large") return "/images/folded-large.png";

  return plan?.image_url || "/images/folded-small.png";
}

function supplierAddress(supplier) {
  return [
    supplier?.indirizzo || supplier?.street,
    supplier?.citta,
    supplier?.provincia ? `(${supplier.provincia})` : "",
  ]
    .filter(Boolean)
    .join(", ");
}

function validDelivery(pickup, delivery) {
  if (!pickup) return true;
  const toMinutes = (value) => {
    const [h, m] = value.split(":").map(Number);
    return h * 60 + m;
  };
  const days = (delivery.day_index - pickup.day_index + 7) % 7;
  return (
    days !== 0 &&
    days * 1440 + toMinutes(delivery.start) - toMinutes(pickup.start) >= 1440
  );
}

function slotKey(slot) {
  if (!slot) return "";
  return `${slot.day_index}|${slot.start}|${slot.end}`;
}

export default function AbbonamentoAttiva() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const { planId } = useParams();

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [planId]);

  const [plan, setPlan] = useState(null);
  const [preferredDays, setPreferredDays] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState("");
  const [selectedSlotKey, setSelectedSlotKey] = useState("");
  const [pickupDay, setPickupDay] = useState("");
  const [deliveryDay, setDeliveryDay] = useState("");
  const [deliverySlotKey, setDeliverySlotKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  const selectedSupplier = useMemo(
    () =>
      suppliers.find(
        (supplier) => String(supplier.id) === String(selectedSupplierId),
      ) || null,
    [suppliers, selectedSupplierId],
  );

  const selectedSlot = useMemo(() => {
    if (!selectedSupplier || !selectedSlotKey) return null;
    return (
      selectedSupplier.pickup_slots?.find(
        (slot) => slotKey(slot) === selectedSlotKey,
      ) || null
    );
  }, [selectedSupplier, selectedSlotKey]);

  const selectedDelivery =
    selectedSupplier?.delivery_slots?.find(
      (slot) => slotKey(slot) === deliverySlotKey,
    ) || null;
  const canPay =
    !!plan &&
    !!selectedSupplier &&
    !!selectedSlot &&
    !!selectedDelivery &&
    validDelivery(selectedSlot, selectedDelivery) &&
    !paying;

  useEffect(() => {
    async function load() {
      if (!token) return;

      setLoading(true);
      try {
        const data = await getSubscriptionActivationDetails(planId, token);
        const nextPlan = data?.plan || null;
        const nextSuppliers = Array.isArray(data?.suppliers)
          ? data.suppliers
          : [];

        setPlan(nextPlan);
        setPreferredDays(
          Array.isArray(data?.preferred_days) ? data.preferred_days : [],
        );
        setSuppliers(nextSuppliers);

        const firstAvailable = nextSuppliers.find(
          (supplier) =>
            Array.isArray(supplier.pickup_slots) &&
            supplier.pickup_slots.length > 0,
        );

        setSelectedSupplierId("");
        setPickupDay("");
        setSelectedSlotKey("");
        setDeliveryDay("");
        setDeliverySlotKey("");
        if (firstAvailable) {
          setSelectedSupplierId(String(firstAvailable.id));
        }
      } catch (err) {
        toast.error(
          err.message || "Errore caricamento attivazione abbonamento",
        );
        setPlan(null);
        setSuppliers([]);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [planId, token]);

  function handleSelectSupplier(supplier) {
    if (String(supplier.id) === selectedSupplierId) return;
    setSelectedSupplierId(String(supplier.id));
    setPickupDay("");
    setSelectedSlotKey("");
    setDeliveryDay("");
    setDeliverySlotKey("");
  }

  function changePickupSlot(key) {
    setSelectedSlotKey(key);
    const pickup = selectedSupplier?.pickup_slots.find(
      (slot) => slotKey(slot) === key,
    );
    if (selectedDelivery && !validDelivery(pickup, selectedDelivery))
      setDeliverySlotKey("");
    if (
      deliveryDay !== "" &&
      !selectedSupplier?.delivery_slots.some(
        (slot) =>
          String(slot.day_index) === deliveryDay && validDelivery(pickup, slot),
      )
    ) {
      setDeliveryDay("");
      setDeliverySlotKey("");
    }
  }

  async function handleCheckout() {
    if (!canPay) {
      toast.error("Seleziona fornitore, ritiro e consegna");
      return;
    }

    setPaying(true);
    try {
      const data = await createSubscriptionCheckoutSession(
        {
          plan_id: Number(planId),
          supplier_id: Number(selectedSupplierId),
          pickup_day_index: selectedSlot.day_index,
          pickup_start: selectedSlot.start,
          pickup_end: selectedSlot.end,
          delivery_day_index: selectedDelivery.day_index,
          delivery_start: selectedDelivery.start,
          delivery_end: selectedDelivery.end,
        },
        token,
      );

      if (!data?.url) {
        throw new Error("URL pagamento non ricevuto");
      }

      window.location.href = data.url;
    } catch (err) {
      toast.error(err.message || "Errore avvio pagamento");
      setPaying(false);
    }
  }

  if (loading) {
    return (
      <main className="cap-page">
        <div className="cap-page-wrapper">
          <section className="sub-activate-page">
            <div className="cap-empty">
              Caricamento attivazione abbonamento...
            </div>
          </section>
        </div>
      </main>
    );
  }

  if (!plan) {
    return (
      <main className="cap-page">
        <div className="cap-page-wrapper">
          <section className="sub-activate-page">
            <div className="cap-empty">
              Piano non trovato o non più disponibile.
            </div>
            <button
              type="button"
              className="cap-btn"
              onClick={() => navigate("/abbonamenti")}
            >
              Torna ai piani
              <span className="cap-btn-arrow">›</span>
            </button>
          </section>
        </div>
      </main>
    );
  }

  const color = getPlanColor(plan);
  const rows = getPlanRows(plan);
  const availableSuppliers = suppliers.filter(
    (supplier) =>
      Array.isArray(supplier.pickup_slots) && supplier.pickup_slots.length > 0,
  );

  return (
    <main className="cap-page">
      <div className="cap-page-wrapper">
        <section className="sub-activate-page">
          <button
            type="button"
            className="sub-back-btn"
            onClick={() => navigate("/abbonamenti")}
          >
            ← Torna al confronto
          </button>

          <section className="sub-activate-hero">
            <div>
              <p className="sub-kicker">Calendario Anti Panico</p>
              <h1>Attiva il tuo piano</h1>
              <p>
                Scegli il fornitore, programma ritiro e consegna settimanali e
                completa il pagamento mensile sicuro con Stripe.
              </p>
            </div>

            <article
              className="sub-plan-summary"
              style={{ "--plan-color": color }}
            >
              <img
                className="sub-plan-img"
                src={imageForPlan(plan)}
                alt=""
                aria-hidden="true"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />

              <h2>{getPlanTitle(plan)}</h2>
              <p>{getPlanShort(plan)}</p>

              {/* Anteprima, coerente con la card di /abbonamenti: SOLO
                  visivo, /4 dal mensile reale. Il pagamento qui sotto
                  (sezione "2. Pagamento") resta mensile, invariato. */}
              <div className="sub-plan-price">
                {euro(plan.price_cents / 4)} / settimana
              </div>

              <div className="sub-plan-table">
                {rows.map((row, index) => (
                  <div className="sub-plan-row" key={`${row.label}-${index}`}>
                    <span>{row.icon || "•"}</span>
                    <em>{row.label}</em>
                    <strong>{row.value}</strong>
                  </div>
                ))}
              </div>
            </article>
          </section>

          <section className="sub-section">
            <div className="sub-section-head">
              <div>
                <h2>1. Scegli il fornitore</h2>
                <p>
                  Mostriamo solo i fornitori associati al piano. Le fasce sono
                  filtrate con i giorni previsti nel piano e con gli orari reali
                  del fornitore.
                </p>
              </div>

              {preferredDays.length > 0 ? (
                <div className="sub-days-box">
                  <span>Giorni piano</span>
                  <strong>
                    {preferredDays.map((day) => day.day_name).join(", ")}
                  </strong>
                </div>
              ) : (
                <div className="sub-days-box">
                  <span>Giorni piano</span>
                  <strong>Tutti quelli disponibili</strong>
                </div>
              )}
            </div>

            {suppliers.length === 0 ? (
              <div className="cap-empty">
                Nessun fornitore è associato a questo piano. Quindi niente
                soldi, niente stiro, solo contemplazione del vuoto.
              </div>
            ) : availableSuppliers.length === 0 ? (
              <div className="cap-empty">
                I fornitori esistono, ma nessuno ha combinazioni di ritiro e
                consegna compatibili con questo piano.
              </div>
            ) : (
              <div className="sub-supplier-list">
                {suppliers.map((supplier) => {
                  const isSelected =
                    String(supplier.id) === String(selectedSupplierId);
                  const hasSlots =
                    Array.isArray(supplier.pickup_slots) &&
                    supplier.pickup_slots.length > 0;

                  const deliveryOptions = (
                    supplier.delivery_slots || []
                  ).filter((slot) =>
                    validDelivery(isSelected ? selectedSlot : null, slot),
                  );
                  const renderSchedule = (kind, title, slots, day, key) => {
                    const days = [
                      ...new Map(
                        slots.map((slot) => [slot.day_index, slot.day_name]),
                      ).entries(),
                    ];
                    return (
                      <fieldset className="sub-schedule" disabled={!hasSlots}>
                        <legend>{title}</legend>
                        <div className="sub-schedule-fields">
                          <label>
                            <select
                              aria-label={`Giorno ${title.toLowerCase()} · ${
                                supplier.nome
                              }`}
                              value={isSelected ? day : ""}
                              onChange={(e) => {
                                handleSelectSupplier(supplier);
                                if (kind === "pickup") {
                                  setPickupDay(e.target.value);
                                  setSelectedSlotKey("");
                                  setDeliveryDay("");
                                  setDeliverySlotKey("");
                                } else {
                                  setDeliveryDay(e.target.value);
                                  setDeliverySlotKey("");
                                }
                              }}
                            >
                              <option value="">Scegli giorno</option>
                              {days.map(([index, name]) => (
                                <option key={index} value={index}>
                                  {name}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            <select
                              aria-label={`Orario ${title.toLowerCase()} · ${
                                supplier.nome
                              }`}
                              value={isSelected ? key : ""}
                              disabled={!isSelected || day === ""}
                              onChange={(e) =>
                                kind === "pickup"
                                  ? changePickupSlot(e.target.value)
                                  : setDeliverySlotKey(e.target.value)
                              }
                            >
                              <option value="">Scegli orario</option>
                              {slots
                                .filter(
                                  (slot) => String(slot.day_index) === day,
                                )
                                .map((slot) => (
                                  <option
                                    key={slotKey(slot)}
                                    value={slotKey(slot)}
                                  >
                                    {slot.start}–{slot.end}
                                  </option>
                                ))}
                            </select>
                          </label>
                        </div>
                      </fieldset>
                    );
                  };
                  return (
                    <article
                      key={supplier.id}
                      className={`sub-supplier-card ${
                        isSelected ? "is-selected" : ""
                      } ${!hasSlots ? "is-disabled" : ""}`}
                      aria-label={`Fornitore ${supplier.nome}`}
                      onClick={() => {
                        if (hasSlots) handleSelectSupplier(supplier);
                      }}
                    >
                      <div className="sub-supplier-main">
                        <SupplierImage
                          className="sub-supplier-photo"
                          src={
                            supplier.cover_url ||
                            supplier.logo_url ||
                            "/images/ciao_stiro.png"
                          }
                          alt={supplier.nome}
                          onError={(e) => {
                            e.currentTarget.src = "/images/ciao_stiro.png";
                          }}
                        />
                        <div className="sub-supplier-info">
                          <div className="sub-supplier-heading">
                            <button
                              type="button"
                              className="sub-supplier-select"
                              aria-pressed={isSelected}
                              disabled={!hasSlots}
                              onClick={() => handleSelectSupplier(supplier)}
                            >
                              <strong>
                                {supplier.nome || `Fornitore #${supplier.id}`}
                              </strong>
                            </button>
                            <StarRating
                              value={supplier.rating_avg}
                              count={supplier.rating_count}
                            />
                          </div>
                          <div className="sub-supplier-details">
                            <div>
                              <span>Indirizzo</span>
                              <strong>
                                {supplierAddress(supplier) || "—"}
                              </strong>
                            </div>
                            <div>
                              <span>Telefono</span>
                              <strong>{supplier.telefono || "—"}</strong>
                            </div>
                            <div>
                              <span>Email</span>
                              <strong>{supplier.email || "—"}</strong>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="sub-slot-panel">
                        {renderSchedule(
                          "pickup",
                          "Ritiro",
                          supplier.pickup_slots || [],
                          pickupDay,
                          selectedSlotKey,
                        )}
                        {renderSchedule(
                          "delivery",
                          "Consegna",
                          deliveryOptions,
                          deliveryDay,
                          deliverySlotKey,
                        )}
                        {!hasSlots && (
                          <p>Nessuna combinazione di ritiro e consegna disponibile.</p>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <section className="sub-pay-card">
            <div>
              <h2>2. Pagamento</h2>
            </div>

            <div className="sub-pay-summary">
              <div>
                <span>Piano</span>
                <strong>{getPlanTitle(plan)}</strong>
              </div>
              <div>
                <span>Fornitore</span>
                <strong>{selectedSupplier?.nome || "—"}</strong>
              </div>
              <div>
                <span>Ritiro</span>
                <strong>{selectedSlot?.label || "—"}</strong>
              </div>
              <div>
                <span>Consegna</span>
                <strong>{selectedDelivery?.label || "—"}</strong>
              </div>
              <div>
                <span>Totale mensile</span>
                <strong>{euro(plan.price_cents)}</strong>
              </div>
            </div>

            <button
              type="button"
              className="sub-pay-btn"
              disabled={!canPay}
              onClick={handleCheckout}
            >
              {paying
                ? "Apro Stripe..."
                : `Paga con Stripe ${euro(plan.price_cents)} / mese`}
              <span aria-hidden="true">›</span>
            </button>
          </section>
        </section>
      </div>
    </main>
  );
}
